/**
 * The My actions menu badge: GET /bp/lifecycle/inbox/?counts_only=1, one shared single-flighted answer.
 * The server filters by the person's roles and readable types; the client only holds the last count.
 * `setLifecycleInboxCounts(counts)` lets the My actions page hand its fresh counts to the badge.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { get_lifecycle_inbox } from '@/utils/lifecycleApi';

const listeners = new Set();
let snapshot = { loaded: false, counts: null };
let inflight = null;

const sessionToken = () => {
  try {
    return localStorage.getItem('Token') || '';
  } catch (e) {
    return '';
  }
};

const emit = () => listeners.forEach((l) => l());

export function setLifecycleInboxCounts(counts, { failed = false } = {}) {
  snapshot = { loaded: true, counts: counts || null, token: sessionToken(), failed };
  emit();
}

export function loadLifecycleInboxCounts({ force = false } = {}) {
  if (inflight && !force) return inflight;
  const request = get_lifecycle_inbox({ counts_only: true })
    .then((r) => setLifecycleInboxCounts(r?.data?.data?.counts || null))
    .catch(() => setLifecycleInboxCounts(null, { failed: true }))
    .finally(() => {
      if (inflight === request) inflight = null;
    });
  inflight = request;
  return request;
}

const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => snapshot;

export function useLifecycleInboxCounts({ enabled = true } = {}) {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const { pathname } = useLocation();
  // Read when not yet read, on a new session (another login: the count is the person's own), and — after a
  // failed read — again on the next navigation or when the window regains focus.
  useEffect(() => {
    if (enabled && (!snap.loaded || snap.failed || snap.token !== sessionToken())) loadLifecycleInboxCounts();
  }, [enabled, snap.loaded, snap.failed, snap.token, pathname]);
  useEffect(() => {
    if (!enabled || !snap.failed) return undefined;
    const onFocus = () => loadLifecycleInboxCounts();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, snap.failed]);
  return snap;
}
